/**
 * Scheduling Service
 *
 * Fetches booking, summary, role-coverage and warning data from the
 * Projects API adapter endpoint which normalises data from the
 * connected resource scheduler (EBScheduler).
 *
 * In demo/tour mode the existing mock data is returned so the UI can
 * be developed without a live backend.  In live mode the real API is called.
 */

import { apiFetch } from '@/lib/api-error-handler';
import { getDataMode, getApiBaseUrl } from '@/lib/data-service';
import { evenMonthlySpread } from '@/lib/resource-budget-window';
import {
  mockSchedulingBookings,
  mockSchedulerConnection,
  mockResourceRequests,
  mockSchedulingSummary,
  mockRoleCoverage,
  mockSchedulingWarnings,
  mockTeamBookingStatuses,
  mockDashboardAlerts,
  mockProjectBookingHealth,
  type SchedulingBooking,
  type ResourceRequest,
  type RoleCoverage,
  type SchedulingWarning,
  type TeamMemberBookingStatus,
  type DashboardSchedulingAlert,
  type ProjectBookingHealth,
} from '@/lib/mock-scheduling-data';

// ---------------------------------------------------------------------------
// Response types — resource request list (GET)
// ---------------------------------------------------------------------------

export interface ResourceRequestResponse {
  requestId: string;
  roleName: string;
  hoursRequired: number;
  allocatedHours: number;
  desiredStartDate: string;
  desiredEndDate?: string;
  priority: string;
  status: string;
  notes?: string;
  raisedAt: string;
  assignedTo?: string;
  assignedResources?: { resourceId: string; name: string; avatarUrl?: string }[];
}

// ---------------------------------------------------------------------------
// Request types — resource request creation
// ---------------------------------------------------------------------------

export interface CreateResourceRequestPayload {
  roleId?: string;
  roleName: string;
  hoursRequired: number;
  desiredStartDate: string; // YYYY-MM-DD
  priority: string; // nice-to-have | important | critical
  notes?: string;
  skills?: Array<{ skillId: string; skillName: string; category: string; requirement: string }>;
  /**
   * Cost Centre mode only — the cost centre the request is raised under. When `roles`
   * is supplied the backend fans the payload out into one request and one demand per
   * role, all tagged with this cost centre. Category mode leaves these unset.
   */
  costCentreId?: number;
  costCentreCode?: string;
  costCentreName?: string;
  roles?: Array<{
    roleId?: string;
    roleName: string;
    hoursRequired: number;
    skills?: Array<{ skillId: string; skillName: string; category: string; requirement: string }>;
  }>;
}

export interface CreatedResourceRequestSummary {
  requestId: string;
  roleName: string;
  schedulerDemandId?: string | null;
  schedulerSyncFailed?: boolean;
}

export interface CreateResourceRequestResponse {
  requestId: string | null;
  status: string;
  message: string | null;
  /**
   * True when the request was saved locally but could not be linked to the
   * Scheduler. Such requests won't update automatically, so the UI should warn.
   */
  schedulerSyncFailed?: boolean;
  /** Optional non-fatal warning describing why the request could not be synced. */
  warning?: string | null;
  /** Cost Centre mode only — one entry per role the batched request fanned out into. */
  createdRequests?: CreatedResourceRequestSummary[] | null;
}

// ---------------------------------------------------------------------------
// Response types (mirror the backend SchedulingBookingsResponse DTO)
// ---------------------------------------------------------------------------

export interface SchedulingBookingsPagination {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface SchedulingBookingsResponse {
  connectionStatus: 'connected' | 'not_configured' | 'error';
  schedulerName: string | null;
  bookings: SchedulingBooking[];
  schedulerUrl?: string | null;
  /** Present only when the caller asked for a page — describes the returned slice. */
  pagination?: SchedulingBookingsPagination | null;
  /** Distinct role names across every booking on the project, not just the returned page. */
  roleOptions?: string[];
  /** Distinct resource names across every booking on the project, not just the returned page. */
  resourceOptions?: string[];
}

/** Server-side paging, filtering and sorting for the bookings list. */
export interface SchedulingBookingsQuery {
  page?: number;
  pageSize?: number;
  /** Free-text match on resource name or role name */
  search?: string;
  /** Booking status, or 'all' */
  status?: string;
  /** Exact role name, or 'all' */
  roleName?: string;
  /** Exact resource (person) name, or 'all' */
  resourceName?: string;
  /** Only bookings ending on or after this date (yyyy-MM-dd) */
  dateFrom?: string;
  /** Only bookings starting on or before this date (yyyy-MM-dd) */
  dateTo?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

// ---------------------------------------------------------------------------
// Response types — summary, role coverage, warnings
// ---------------------------------------------------------------------------

export interface UnmetRoleResponse {
  roleId: string;
  roleName: string;
  budgetHours: number;
  scheduledHours: number;
  gapHours: number;
}

export interface SummaryWarningResponse {
  type: string;
  message: string;
}

export interface SchedulingSummaryResponse {
  totalBudgetHours: number;
  scheduledHours: number;
  bookingCoveragePercent: number;
  confirmedBookings: number;
  plannedBookings: number;
  totalBookings: number;
  unmetDemandHours: number;
  unmetDemandRoles: number;
  healthStatus: string;
  unmetRoles: UnmetRoleResponse[];
  warnings: SummaryWarningResponse[];
  bookingsEndDate: string | null;
  projectEndDate: string | null;
}

export interface RoleCoveragePersonResponse {
  personId: string;
  personName: string;
  scheduledHours: number;
  actualHours: number;
  status: string;
}

export interface RoleCoverageItemResponse {
  roleId: string;
  roleName: string;
  budgetHours: number;
  scheduledHours: number;
  /** scheduledHours bucketed by calendar month (YYYY-MM → hours) */
  scheduledHoursByMonth?: Record<string, number>;
  actualHours: number;
  remainingHours: number;
  requestedHours: number;
  /** requestedHours bucketed by calendar month (YYYY-MM → hours) */
  requestedHoursByMonth?: Record<string, number>;
  remainingBudgetHours: number;
  coveragePercent: number;
  status: string;
  hasPendingRequest: boolean;
  people: RoleCoveragePersonResponse[];
}

export interface RoleCoverageResponse {
  roles: RoleCoverageItemResponse[];
}

export interface MonthlyBreakdownResponse {
  /** Month key in YYYY-MM format */
  month: string;
  budgetHours: number;
}

export interface SchedulingWarningResponse {
  id: string;
  type: string;
  severity: string;
  message: string;
  roleName?: string;
  gapHours?: number;
}

export interface SchedulingWarningsResponse {
  warnings: SchedulingWarningResponse[];
}

// ---------------------------------------------------------------------------
// Request/response types — booking update
// ---------------------------------------------------------------------------

export interface UpdateBookingPayload {
  startTime?: string;
  endTime?: string;
  durationHours?: number;
  status?: string;
  priority?: string;
  bookingNotes?: string;
  resourceId?: number;
}

// ---------------------------------------------------------------------------
// Service function
// ---------------------------------------------------------------------------

/**
 * Fetch normalised scheduling bookings for a project.
 *
 * - demo / tour → returns mock data immediately
 * - live        → calls GET /api/projects/{projectId}/scheduling/bookings
 */
export async function fetchSchedulingBookings(
  projectId: string,
  query?: SchedulingBookingsQuery,
): Promise<SchedulingBookingsResponse> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    // Simulate a small network delay so loading states are visible during dev
    await new Promise((resolve) => setTimeout(resolve, 300));
    const { bookings, pagination } = applyBookingsQuery(mockSchedulingBookings, query);
    return {
      connectionStatus: mockSchedulerConnection.status === 'connected' ? 'connected' : 'not_configured',
      schedulerName: mockSchedulerConnection.displayName,
      bookings,
      schedulerUrl: mockSchedulerConnection.schedulerUrl ?? null,
      pagination,
      roleOptions: distinctRoleNames(mockSchedulingBookings),
      resourceOptions: distinctResourceNames(mockSchedulingBookings),
    };
  }

  // Live mode — call the real backend adapter endpoint
  const params = buildBookingsQueryParams(query);
  const url = `${getApiBaseUrl()}/projects/${projectId}/scheduling/bookings${params ? `?${params}` : ''}`;
  const data = await apiFetch<SchedulingBookingsResponse>(url);
  return data;
}

function buildBookingsQueryParams(query?: SchedulingBookingsQuery): string {
  if (!query) return '';
  const params = new URLSearchParams();
  if (query.page !== undefined) params.set('page', String(query.page));
  if (query.pageSize !== undefined) params.set('pageSize', String(query.pageSize));
  if (query.search) params.set('search', query.search);
  if (query.status && query.status !== 'all') params.set('status', query.status);
  if (query.roleName && query.roleName !== 'all') params.set('roleName', query.roleName);
  if (query.resourceName && query.resourceName !== 'all') params.set('resourceName', query.resourceName);
  if (query.dateFrom) params.set('dateFrom', query.dateFrom);
  if (query.dateTo) params.set('dateTo', query.dateTo);
  if (query.sortBy) params.set('sortBy', query.sortBy);
  if (query.sortDirection) params.set('sortDirection', query.sortDirection);
  return params.toString();
}

function distinctRoleNames(bookings: SchedulingBooking[]): string[] {
  return [...new Set(bookings.map((booking) => booking.roleName).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b));
}

function distinctResourceNames(bookings: SchedulingBooking[]): string[] {
  return [...new Set(bookings.map((booking) => booking.personName).filter((name): name is string => Boolean(name)))]
    .sort((a, b) => a.localeCompare(b));
}

/** Date-only portion of a booking date, falling back to the other end of the booking. */
function bookingDateOrFallback(preferred: string, fallback: string): string {
  return (preferred || fallback).slice(0, 10);
}

/**
 * Mirrors the backend filter/sort/page behaviour for mock data so demo and tour
 * mode exercise the same paged code path as live mode.
 */
function applyBookingsQuery(
  bookings: SchedulingBooking[],
  query?: SchedulingBookingsQuery,
): { bookings: SchedulingBooking[]; pagination: SchedulingBookingsPagination | null } {
  const searchTerm = (query?.search ?? '').trim().toLowerCase();
  const status = query?.status;
  const roleName = query?.roleName;
  const resourceName = query?.resourceName;
  const dateFrom = query?.dateFrom;
  const dateTo = query?.dateTo;

  const matching = bookings.filter((booking) => (
    (searchTerm === ''
      || (booking.personName ?? '').toLowerCase().includes(searchTerm)
      || booking.roleName.toLowerCase().includes(searchTerm))
    && (!status || status === 'all' || booking.status === status)
    && (!roleName || roleName === 'all' || booking.roleName === roleName)
    && (!resourceName || resourceName === 'all' || booking.personName === resourceName)
    && (!dateFrom || bookingDateOrFallback(booking.endDate, booking.startDate) >= dateFrom)
    && (!dateTo || bookingDateOrFallback(booking.startDate, booking.endDate) <= dateTo)
  ));

  const sortBy = query?.sortBy;
  if (sortBy) {
    const mult = query?.sortDirection === 'desc' ? -1 : 1;
    const numericColumns = new Set(['totalHours', 'actualHours']);
    matching.sort((a, b) => {
      const aVal = a[sortBy as keyof SchedulingBooking];
      const bVal = b[sortBy as keyof SchedulingBooking];
      return numericColumns.has(sortBy)
        ? ((Number(aVal) || 0) - (Number(bVal) || 0)) * mult
        : String(aVal ?? '').localeCompare(String(bVal ?? '')) * mult;
    });
  }

  if (query?.page === undefined && query?.pageSize === undefined) {
    return { bookings: matching, pagination: null };
  }

  const pageSize = Math.max(1, query.pageSize ?? 25);
  const totalPages = Math.ceil(matching.length / pageSize);
  const currentPage = Math.min(Math.max(1, query.page ?? 1), Math.max(totalPages, 1));

  return {
    bookings: matching.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    pagination: { currentPage, pageSize, totalItems: matching.length, totalPages },
  };
}

// ---------------------------------------------------------------------------
// Create resource request
// ---------------------------------------------------------------------------

/**
 * Create a resource request (unallocated demand booking) in the connected scheduler.
 *
 * - demo / tour → simulates success after a short delay
 * - live        → calls POST /api/projects/{projectId}/scheduling/requests
 */
/**
 * Fetch all resource requests for a project.
 *
 * - demo / tour → returns mock data immediately
 * - live        → calls GET /api/projects/{projectId}/scheduling/requests
 */
export async function fetchResourceRequests(
  projectId: string,
): Promise<ResourceRequestResponse[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise((resolve) => setTimeout(resolve, 300));
    // Map mock data to the response shape
    return mockResourceRequests.map((r: ResourceRequest) => ({
      requestId: r.requestId,
      roleName: r.roleName,
      hoursRequired: r.hoursRequired,
      allocatedHours: r.allocatedHours,
      desiredStartDate: r.startDate,
      desiredEndDate: r.endDate,
      priority: r.priority,
      status: r.status,
      notes: r.notes,
      raisedAt: r.raisedAt,
      assignedTo: r.assignedTo,
      assignedResources:
        r.assignedResources ??
        (r.assignedTo
          ? r.assignedTo
              .split(', ')
              .map((name) => name.trim())
              .filter((name) => name.length > 0)
              .map((name, index) => ({ resourceId: `${r.requestId}-${index}`, name, avatarUrl: undefined }))
          : undefined),
    }));
  }

  const url = `${getApiBaseUrl()}/projects/${projectId}/scheduling/requests`;
  const data = await apiFetch<ResourceRequestResponse[]>(url);
  return data;
}

// ---------------------------------------------------------------------------
// Summary, Role Coverage, Warnings
// ---------------------------------------------------------------------------

export async function fetchSchedulingSummary(
  projectId: string,
): Promise<SchedulingSummaryResponse> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return {
      totalBudgetHours: mockSchedulingSummary.totalBudgetHours,
      scheduledHours: mockSchedulingSummary.totalScheduledHours,
      bookingCoveragePercent: mockSchedulingSummary.coveragePercent,
      confirmedBookings: mockSchedulingSummary.confirmedBookings,
      plannedBookings: mockSchedulingSummary.plannedBookings,
      totalBookings: mockSchedulingSummary.totalBookings,
      unmetDemandHours: mockSchedulingSummary.unmetRoles.reduce((sum, r) => sum + r.gapHours, 0),
      unmetDemandRoles: mockSchedulingSummary.unmetRoles.length,
      healthStatus: mockSchedulingSummary.healthStatus,
      unmetRoles: mockSchedulingSummary.unmetRoles,
      warnings: mockSchedulingSummary.warnings.map((w) => ({ type: w.type, message: w.message })),
      bookingsEndDate: mockSchedulingSummary.bookingsEndDate,
      projectEndDate: mockSchedulingSummary.projectEndDate,
    };
  }

  const url = `${getApiBaseUrl()}/projects/${projectId}/scheduling/summary`;
  return await apiFetch<SchedulingSummaryResponse>(url);
}

export async function fetchRoleCoverage(
  projectId: string,
): Promise<RoleCoverage[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return mockRoleCoverage;
  }

  const url = `${getApiBaseUrl()}/projects/${projectId}/scheduling/role-coverage`;
  const data = await apiFetch<RoleCoverageResponse>(url);
  return data.roles.map((r) => ({
    roleId: r.roleId,
    roleName: r.roleName,
    budgetHours: r.budgetHours,
    scheduledHours: r.scheduledHours,
    scheduledHoursByMonth: r.scheduledHoursByMonth,
    actualHours: r.actualHours,
    remainingHours: r.remainingHours,
    requestedHours: r.requestedHours ?? 0,
    requestedHoursByMonth: r.requestedHoursByMonth,
    remainingBudgetHours:
      r.remainingBudgetHours ??
      Math.max(0, r.budgetHours - r.scheduledHours - (r.requestedHours ?? 0)),
    coveragePercent: r.coveragePercent,
    status: r.status as RoleCoverage['status'],
    hasPendingRequest: r.hasPendingRequest,
    people: r.people.map((p) => ({
      personId: p.personId,
      personName: p.personName,
      scheduledHours: p.scheduledHours,
      actualHours: p.actualHours,
    })),
  }));
}

/**
 * Fetch the monthly budget curve for a single role on a project.
 *
 * - demo / tour → derives an even spread from the mock role budget so the drawer's
 *   "remaining from today" prefill still has month-level data to work with
 * - live        → calls GET /api/projects/{projectId}/scheduling/resource-budget/{roleId}/monthly-breakdown
 */
export async function fetchRoleMonthlyBreakdown(
  projectId: string,
  roleId: string,
): Promise<MonthlyBreakdownResponse[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise((resolve) => setTimeout(resolve, 150));
    const role = mockRoleCoverage.find((r) => r.roleId === roleId);
    if (!role) return [];
    const start = new Date();
    const end = new Date(start.getFullYear(), start.getMonth() + 5, 1);
    return evenMonthlySpread(role.budgetHours, start, end);
  }

  const url = `${getApiBaseUrl()}/projects/${projectId}/scheduling/resource-budget/${roleId}/monthly-breakdown`;
  const data = await apiFetch<MonthlyBreakdownResponse[]>(url);
  return data ?? [];
}

export async function fetchSchedulingWarnings(
  projectId: string,
): Promise<SchedulingWarning[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return mockSchedulingWarnings;
  }

  const url = `${getApiBaseUrl()}/projects/${projectId}/scheduling/warnings`;
  const data = await apiFetch<SchedulingWarningsResponse>(url);
  return data.warnings.map((w) => ({
    type: w.type as SchedulingWarning['type'],
    severity: w.severity as SchedulingWarning['severity'],
    message: w.message,
  }));
}

// ---------------------------------------------------------------------------
// Booking update / cancel
// ---------------------------------------------------------------------------

export async function updateBooking(
  projectId: string,
  bookingId: string,
  payload: UpdateBookingPayload,
): Promise<SchedulingBooking> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise((resolve) => setTimeout(resolve, 500));
    const existing = mockSchedulingBookings.find((b) => b.bookingId === bookingId);
    return existing ?? mockSchedulingBookings[0];
  }

  const url = `${getApiBaseUrl()}/projects/${projectId}/scheduling/bookings/${bookingId}`;
  return await apiFetch<SchedulingBooking>(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function cancelBooking(
  projectId: string,
  bookingId: string,
): Promise<void> {
  await updateBooking(projectId, bookingId, { status: 'cancelled' });
}

export async function createResourceRequest(
  projectId: string,
  payload: CreateResourceRequestPayload,
  options?: { useDemandFlow?: boolean },
): Promise<CreateResourceRequestResponse> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise((resolve) => setTimeout(resolve, 800));
    return {
      requestId: `mock-${Date.now()}`,
      status: 'Pending',
      message: `Resource request created for ${payload.roleName}.`,
    };
  }

  let url = `${getApiBaseUrl()}/projects/${projectId}/scheduling/requests`;
  if (options?.useDemandFlow) {
    url += '?useDemandFlow=true';
  }
  const data = await apiFetch<CreateResourceRequestResponse>(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return data;
}

// ---------------------------------------------------------------------------
// Team booking statuses
// ---------------------------------------------------------------------------

export async function fetchTeamBookingStatuses(
  projectId: string,
): Promise<TeamMemberBookingStatus[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return mockTeamBookingStatuses;
  }

  const url = `${getApiBaseUrl()}/projects/${projectId}/team/booking-statuses`;
  const data = await apiFetch<TeamMemberBookingStatus[]>(url);
  return data;
}

// ---------------------------------------------------------------------------
// Dashboard scheduling alerts
// ---------------------------------------------------------------------------

export async function fetchSchedulingAlerts(): Promise<DashboardSchedulingAlert[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return mockDashboardAlerts;
  }

  const url = `${getApiBaseUrl()}/dashboard/scheduling-alerts`;
  const data = await apiFetch<DashboardSchedulingAlert[]>(url);
  return data;
}

// ---------------------------------------------------------------------------
// Project scheduling health (bulk)
// ---------------------------------------------------------------------------

export async function fetchSchedulingHealth(): Promise<ProjectBookingHealth[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return mockProjectBookingHealth;
  }

  const url = `${getApiBaseUrl()}/projects/scheduling-health`;
  const data = await apiFetch<ProjectBookingHealth[]>(url);
  return data;
}
